import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate, fmtDateOnly, fmtTimeOnly } from "../lib/api";
import { toast } from "sonner";
import {
  Users, MessageSquare, Paperclip, Send, Download, RefreshCw, Trash2,
  Mail, X, Plus, ChevronRight, FileText,
} from "lucide-react";

// b64 → download helper
function downloadB64(b64, mime, filename) {
  try {
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "file";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("DOWNLOAD_FAILED", { description: String(e.message || e) });
  }
}

async function fileToBase64(file) {
  const buf = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export default function CollabPage() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [activeId, setActiveId] = useState(params.get("space") || null);
  const [tab, setTab] = useState("chat"); // chat | notes | library

  const { data: spacesData } = useQuery({
    queryKey: ["collab-spaces"],
    queryFn: () => api.get("/collab/spaces").then((r) => r.data),
    refetchInterval: 15000,
  });
  const spaces = spacesData?.spaces || [];

  const { data: inbox } = useQuery({
    queryKey: ["collab-inbox"],
    queryFn: () => api.get("/collab/inbox").then((r) => r.data),
    refetchInterval: 20000,
  });

  useEffect(() => {
    if (!activeId && spaces.length) setActiveId(spaces[0].id);
  }, [spaces, activeId]);

  const active = spaces.find((s) => s.id === activeId);

  const refreshInbox = useMutation({
    mutationFn: () => api.post("/collab/refresh").then((r) => r.data),
    onSuccess: (d) => {
      toast.success("LIBRARY_REFRESHED", {
        description: `${d.imported} new files pulled into your library`,
      });
      qc.invalidateQueries({ queryKey: ["collab-inbox"] });
      qc.invalidateQueries({ queryKey: ["saves"] });
      qc.invalidateQueries({ queryKey: ["saves-stats"] });
    },
    onError: (e) => {
      toast.error("REFRESH_FAILED", { description: e?.response?.data?.detail || e?.message });
    },
  });

  return (
    <div data-testid="collab-page" className="p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-mono inline-flex items-center gap-2">
            <Users size={12} /> COLLABORATION_WORKSPACES
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">Collab</h1>
          <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl">
            Private 1:1 workspaces. Chat, notes, and a shared library. Every
            upload lands in each collaborator&apos;s personal library on{" "}
            <span className="text-emerald-300">REFRESH</span>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="collab-refresh-inbox"
            onClick={() => refreshInbox.mutate()}
            disabled={refreshInbox.isPending}
            className="inline-flex items-center gap-2 h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-emerald-400/60 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
          >
            <RefreshCw size={12} className={refreshInbox.isPending ? "animate-spin" : ""} />
            REFRESH_LIBRARY ({inbox?.pending ?? 0})
          </button>
          <NewSpaceButton />
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Sidebar */}
        <aside className="lg:col-span-3 border border-[#27272A] bg-[#0E0E0E]">
          <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            SPACES · {spaces.length}
          </div>
          {spaces.length === 0 ? (
            <div className="p-4 text-[11px] font-mono text-zinc-500">
              NO_SPACES · click NEW_SPACE to invite someone
            </div>
          ) : (
            <ul>
              {spaces.map((s) => (
                <li key={s.id}>
                  <button
                    data-testid={`collab-space-${s.id}`}
                    onClick={() => setActiveId(s.id)}
                    className={`w-full text-left px-3 py-2 border-b border-[#161616] hover:bg-[#141414] flex items-center gap-2 ${
                      activeId === s.id ? "bg-[#141414]" : ""
                    }`}
                  >
                    <span
                      className={`inline-block w-1.5 h-1.5 ${
                        s.status === "active" ? "bg-emerald-400" : "bg-amber-400"
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-mono text-white truncate">
                        {s.name}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {s.partner_email} · {s.status}
                      </div>
                    </div>
                    <ChevronRight size={12} className="text-zinc-600" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Main panel */}
        <main className="lg:col-span-9 border border-[#27272A] bg-[#0A0A0A] min-h-[520px]">
          {!active ? (
            <div className="p-12 text-center text-[12px] font-mono text-zinc-500 uppercase tracking-wider">
              SELECT_OR_CREATE_A_SPACE
            </div>
          ) : (
            <ActivePanel space={active} tab={tab} setTab={setTab} />
          )}
        </main>
      </div>
    </div>
  );
}

function NewSpaceButton() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.post("/collab/invite", { partner_email: email, name })
        .then((r) => r.data),
    onSuccess: (d) => {
      toast.success("INVITE_SENT", {
        description: `Link copied to clipboard · shared with ${d.partner_email}`,
      });
      if (d.invite_link) navigator.clipboard?.writeText(d.invite_link).catch(() => {});
      qc.invalidateQueries({ queryKey: ["collab-spaces"] });
      setOpen(false);
      setName("");
      setEmail("");
    },
    onError: (e) => {
      toast.error("INVITE_FAILED", { description: e?.response?.data?.detail || e?.message });
    },
  });

  return (
    <>
      <button
        data-testid="collab-new-space"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400 text-black hover:bg-amber-300"
      >
        <Plus size={12} /> NEW_SPACE
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md border border-amber-400/60 bg-[#0A0A0A] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-300">
                NEW_COLLAB_SPACE
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white">
                <X size={14} />
              </button>
            </div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-2">
              SPACE_NAME
            </label>
            <input
              data-testid="collab-invite-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q4 research pod"
              className="w-full mt-1 h-9 px-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-amber-400 outline-none"
            />
            <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3">
              PARTNER_EMAIL
            </label>
            <input
              data-testid="collab-invite-email"
              value={email}
              type="email"
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@lab.org"
              className="w-full mt-1 h-9 px-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-amber-400 outline-none"
            />
            <button
              data-testid="collab-invite-submit"
              onClick={() => create.mutate()}
              disabled={!email || !name || create.isPending}
              className="w-full mt-4 h-10 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              {create.isPending ? "SENDING…" : "SEND_INVITE"}
            </button>
            <p className="text-[10px] font-mono text-zinc-600 mt-2 leading-relaxed">
              A signed invite link will be created. Share it with your partner —
              they must sign in with the same email to accept.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function ActivePanel({ space, tab, setTab }) {
  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[#27272A] px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[13px] font-mono text-white truncate">{space.name}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">
            {space.owner_email} ↔ {space.partner_email} · {space.status}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {["chat", "notes", "library", "roster"].map((k) => (
            <button
              key={k}
              data-testid={`collab-tab-${k}`}
              onClick={() => setTab(k)}
              className={`h-8 px-3 text-[10px] font-mono uppercase tracking-wider border ${
                tab === k
                  ? "border-amber-400 text-amber-300 bg-amber-500/10"
                  : "border-[#27272A] text-zinc-400 hover:text-white"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      {tab === "chat" && <ChatPanel space={space} />}
      {tab === "notes" && <NotesPanel space={space} />}
      {tab === "library" && <LibraryPanel space={space} />}
      {tab === "roster" && <RosterPanel space={space} />}
    </div>
  );
}

function ChatPanel({ space }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const { data } = useQuery({
    queryKey: ["collab-msgs", space.id],
    queryFn: () => api.get(`/collab/spaces/${space.id}/messages`).then((r) => r.data),
    refetchInterval: 6000,
  });
  const msgs = data?.messages || [];
  const send = useMutation({
    mutationFn: () =>
      api.post(`/collab/spaces/${space.id}/messages`, { body: text })
        .then((r) => r.data),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["collab-msgs", space.id] });
    },
    onError: (e) => toast.error("SEND_FAILED", { description: e?.response?.data?.detail || e?.message }),
  });

  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs.length]);

  return (
    <div className="flex flex-col h-[520px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {msgs.length === 0 && (
          <div className="text-[11px] font-mono text-zinc-500 text-center py-10">
            NO_MESSAGES_YET
          </div>
        )}
        {msgs.map((m) => (
          <div
            key={m.id}
            data-testid={`collab-msg-${m.id}`}
            className={`border-l-2 pl-3 ${m.is_owner ? "border-amber-400/70" : "border-[#27272A]"}`}
          >
            <div className="text-[10px] font-mono uppercase text-zinc-500 tracking-wider flex items-center gap-1">
              {m.is_owner && (
                <span
                  data-testid={`collab-admin-badge-${m.id}`}
                  className="inline-flex items-center justify-center h-4 w-4 border border-amber-400 text-amber-300 font-mono text-[9px] font-extrabold mr-0.5"
                  title="Admin (owner)"
                >
                  A
                </span>
              )}
              <span>{m.display_name || m.sender_email}</span>
              <span>·</span>
              <span>{fmtTimeOnly(m.sent_at)}</span>
            </div>
            <div className="text-[13px] text-white whitespace-pre-wrap mt-0.5">
              {m.body}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-[#27272A] p-2 flex items-center gap-2">
        <input
          data-testid="collab-msg-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && text.trim() && send.mutate()}
          placeholder="Type a message…"
          className="flex-1 h-10 px-2 bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white focus:border-amber-400 outline-none"
        />
        <button
          data-testid="collab-msg-send"
          onClick={() => text.trim() && send.mutate()}
          disabled={!text.trim() || send.isPending}
          className="h-10 px-4 inline-flex items-center gap-2 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
        >
          <Send size={12} /> SEND
        </button>
      </div>
    </div>
  );
}

function NotesPanel({ space }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["collab-notes", space.id],
    queryFn: () => api.get(`/collab/spaces/${space.id}/notes`).then((r) => r.data),
  });
  const [body, setBody] = useState("");
  useEffect(() => { if (data?.body !== undefined) setBody(data.body); }, [data?.body]);
  const save = useMutation({
    mutationFn: () =>
      api.put(`/collab/spaces/${space.id}/notes`, { body }).then((r) => r.data),
    onSuccess: () => {
      toast.success("NOTES_SAVED");
      qc.invalidateQueries({ queryKey: ["collab-notes", space.id] });
    },
    onError: (e) => toast.error("SAVE_FAILED", { description: e?.response?.data?.detail || e?.message }),
  });
  return (
    <div className="p-3 space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
        SHARED_NOTES · last edit {data?.updated_at ? fmtDate(data.updated_at) : "—"}
      </div>
      <textarea
        data-testid="collab-notes-input"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={16}
        placeholder="Drop research notes, plans, next steps… both members can edit."
        className="w-full bg-[#0E0E0E] border border-[#27272A] text-[13px] font-mono text-white p-3 focus:border-amber-400 outline-none"
      />
      <button
        data-testid="collab-notes-save"
        onClick={() => save.mutate()}
        disabled={save.isPending}
        className="h-9 px-3 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
      >
        {save.isPending ? "SAVING…" : "SAVE_NOTES"}
      </button>
    </div>
  );
}

function LibraryPanel({ space }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["collab-files", space.id],
    queryFn: () => api.get(`/collab/spaces/${space.id}/files`).then((r) => r.data),
    refetchInterval: 15000,
  });
  const files = data?.files || [];

  return (
    <div className="p-3 space-y-3">
      <UploadForm space={space} />
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-[#27272A] pb-1">
        SHARED_FILES · {files.length}
      </div>
      {files.length === 0 ? (
        <div className="text-[11px] font-mono text-zinc-500 text-center py-8">
          NO_FILES_YET · use the upload form above
        </div>
      ) : (
        <ul className="space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              data-testid={`collab-file-${f.id}`}
              className="border border-[#27272A] bg-[#0E0E0E] p-3 flex items-center gap-3"
            >
              <FileText size={16} className="text-emerald-300" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-white truncate">{f.filename}</div>
                <div className="text-[10px] font-mono text-zinc-500 truncate">
                  by {f.uploader_email} · {(f.size / 1024).toFixed(1)} KB ·
                  {" "}{fmtDate(f.uploaded_at)}
                </div>
                {f.description && (
                  <div className="text-[11px] text-zinc-400 mt-1 line-clamp-2">
                    {f.description}
                  </div>
                )}
              </div>
              <button
                data-testid={`collab-file-download-${f.id}`}
                onClick={async () => {
                  try {
                    const r = await api.get(`/collab/spaces/${space.id}/files/${f.id}`);
                    downloadB64(r.data.body_b64, r.data.mime, r.data.filename);
                  } catch (e) {
                    toast.error("DOWNLOAD_FAILED", { description: e?.response?.data?.detail || e?.message });
                  }
                }}
                className="h-8 px-3 inline-flex items-center gap-1 border border-[#27272A] text-zinc-300 hover:border-emerald-400 hover:text-white text-[10px] font-mono uppercase tracking-wider"
              >
                <Download size={11} /> GET
              </button>
            </li>
          ))}
        </ul>
      )}
      <PullInboxRow spaceId={space.id} onRefreshed={() => {
        qc.invalidateQueries({ queryKey: ["saves"] });
        qc.invalidateQueries({ queryKey: ["saves-stats"] });
      }} />
    </div>
  );
}

function UploadForm({ space }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [description, setDescription] = useState("");

  const upload = useMutation({
    mutationFn: async () => {
      const b64 = await fileToBase64(file);
      return api.post(`/collab/spaces/${space.id}/files`, {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        body_b64: b64,
        description,
        recipient_emails: recipients.split(",").map((s) => s.trim()).filter(Boolean),
      }).then((r) => r.data);
    },
    onSuccess: (d) => {
      toast.success("UPLOADED_AND_SENT", { description: `${d.filename} (${(d.size/1024).toFixed(1)} KB)` });
      setFile(null); setDescription(""); setRecipients("");
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["collab-files", space.id] });
      qc.invalidateQueries({ queryKey: ["collab-msgs", space.id] });
    },
    onError: (e) => toast.error("UPLOAD_FAILED", { description: e?.response?.data?.detail || e?.message }),
  });

  const defaultRecipients = [space.owner_email, space.partner_email].filter(Boolean).join(", ");

  return (
    <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-300">
        <Paperclip size={12} /> UPLOAD_AND_SEND
      </div>
      <input
        ref={fileRef}
        type="file"
        data-testid="collab-file-input"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="w-full text-[11px] font-mono text-zinc-300 file:mr-3 file:h-8 file:px-3 file:border file:border-[#27272A] file:bg-[#0E0E0E] file:text-emerald-300 file:cursor-pointer"
      />
      <div className="flex items-center gap-2">
        <Mail size={11} className="text-zinc-500" />
        <input
          data-testid="collab-recipients-input"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder={`Recipients (comma-separated) — defaults to ${defaultRecipients}`}
          className="flex-1 h-9 px-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-emerald-400 outline-none"
        />
      </div>
      <input
        data-testid="collab-description-input"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description / message (optional)"
        className="w-full h-9 px-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-emerald-400 outline-none"
      />
      <button
        data-testid="collab-file-send"
        onClick={() => upload.mutate()}
        disabled={!file || upload.isPending}
        className="h-9 px-4 inline-flex items-center gap-2 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
      >
        <Send size={12} /> {upload.isPending ? "SENDING…" : "SEND_TO_LIBRARIES"}
      </button>
      <p className="text-[10px] font-mono text-zinc-500 leading-relaxed">
        Recipients get the file dropped into their MindStein personal library
        on their next REFRESH. Max 900 KB / file.
      </p>
    </div>
  );
}

function PullInboxRow({ spaceId, onRefreshed }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["collab-inbox"],
    queryFn: () => api.get("/collab/inbox").then((r) => r.data),
  });
  const refresh = useMutation({
    mutationFn: () => api.post("/collab/refresh").then((r) => r.data),
    onSuccess: (d) => {
      toast.success("PULLED_INTO_LIBRARY", { description: `${d.imported} new item(s)` });
      qc.invalidateQueries({ queryKey: ["collab-inbox"] });
      onRefreshed?.();
    },
  });
  const pending = data?.pending || 0;
  if (!pending) return null;
  return (
    <div className="mt-3 border border-amber-400/60 bg-amber-500/10 p-3 flex items-center gap-3">
      <div className="text-[11px] font-mono uppercase tracking-wider text-amber-200 flex-1">
        {pending} NEW_FILE(S)_WAITING_IN_YOUR_INBOX
      </div>
      <button
        data-testid="collab-inbox-refresh"
        onClick={() => refresh.mutate()}
        disabled={refresh.isPending}
        className="h-8 px-3 inline-flex items-center gap-1 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
      >
        <RefreshCw size={11} className={refresh.isPending ? "animate-spin" : ""} /> PULL_ALL
      </button>
    </div>
  );
}


function RosterPanel({ space }) {
  const qc = useQueryClient();
  const [newEmails, setNewEmails] = useState("");

  const { data: roster } = useQuery({
    queryKey: ["collab-roster", space.id],
    queryFn: () =>
      api.get(`/collab/spaces/${space.id}/members`).then((r) => r.data),
    refetchInterval: 15_000,
  });

  const inviteMore = useMutation({
    mutationFn: (emails) =>
      api.post(`/collab/spaces/${space.id}/invite-more`, { emails })
        .then((r) => r.data),
    onSuccess: (d) => {
      setNewEmails("");
      const added = (d?.pending_invites || []).length;
      toast.success(`INVITE_MORE · ${added} pending`);
      qc.invalidateQueries({ queryKey: ["collab-roster", space.id] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "INVITE_MORE_FAILED"),
  });

  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const rename = useMutation({
    mutationFn: () =>
      api.post(`/collab/spaces/${space.id}/rename`, { display_name: newName })
        .then((r) => r.data),
    onSuccess: () => {
      toast.success("DISPLAY_NAME_UPDATED");
      setRenameOpen(false); setNewName("");
      qc.invalidateQueries({ queryKey: ["collab-roster", space.id] });
      qc.invalidateQueries({ queryKey: ["collab-msgs", space.id] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "RENAME_FAILED"),
  });

  return (
    <div className="flex flex-col p-3 gap-3 h-[520px] overflow-y-auto">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 mb-2">
          ROSTER · {(roster?.members?.length || 0) + 2} MEMBERS
        </div>
        <div className="space-y-1.5">
          <RosterRow
            label="Owner"
            email={roster?.owner?.email}
            name={roster?.owner?.display_name}
            badge="A"
            tone="amber"
          />
          <RosterRow
            label={roster?.partner?.status === "active" ? "Partner" : "Partner · Pending"}
            email={roster?.partner?.email}
            name={roster?.partner?.display_name}
            tone="purple"
          />
          {(roster?.members || []).map((m) => (
            <RosterRow
              key={m.user_id}
              label="Member"
              email={m.email}
              name={m.display_name}
              tone="cyan"
              testid={`roster-member-${m.email}`}
            />
          ))}
        </div>
      </div>

      {(roster?.pending_invites || []).length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
            PENDING_INVITES · {roster.pending_invites.length}
          </div>
          <div className="space-y-1">
            {roster.pending_invites.map((i) => (
              <div
                key={i.token}
                data-testid={`roster-pending-${i.email}`}
                className="border border-zinc-800 bg-[#0A0A0A] px-2 py-1.5 text-[11.5px] font-mono text-zinc-300 flex items-center justify-between gap-2"
              >
                <span className="truncate">{i.email}</span>
                <span className="text-[9px] text-zinc-500 uppercase tracking-wider">
                  {fmtDateOnly(i.invited_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-[#27272A] pt-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300 mb-2">
          INVITE_MORE_MEMBERS
        </div>
        <div className="text-[10.5px] text-zinc-500 mb-2">
          Comma or newline separated · owner-only.
        </div>
        <textarea
          data-testid="collab-invite-more-input"
          value={newEmails}
          onChange={(e) => setNewEmails(e.target.value)}
          placeholder="alice@example.com, bob@example.com"
          rows={2}
          className="w-full px-2 py-1.5 bg-[#0E0E0E] border border-zinc-700 focus:border-emerald-400 outline-none text-[12px] font-mono text-white resize-none"
        />
        <button
          data-testid="collab-invite-more-btn"
          onClick={() => {
            const parts = newEmails.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
            if (!parts.length) {
              toast.error("ENTER_AT_LEAST_ONE_EMAIL");
              return;
            }
            inviteMore.mutate(parts);
          }}
          disabled={inviteMore.isPending}
          className="mt-2 h-9 px-3 border border-emerald-400 bg-emerald-400 text-black hover:bg-emerald-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50 inline-flex items-center gap-2"
        >
          {inviteMore.isPending ? "INVITING…" : "SEND_INVITES"}
        </button>
      </div>

      <div className="border-t border-[#27272A] pt-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
          MY_DISPLAY_NAME
        </div>
        {renameOpen ? (
          <div className="flex gap-2">
            <input
              data-testid="collab-rename-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="How teammates will see you"
              className="flex-1 h-9 px-2 bg-[#0E0E0E] border border-zinc-700 focus:border-amber-400 outline-none text-[12px] font-mono text-white"
            />
            <button
              data-testid="collab-rename-save"
              onClick={() => newName.trim() && rename.mutate()}
              disabled={rename.isPending || !newName.trim()}
              className="h-9 px-3 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              SAVE
            </button>
            <button
              onClick={() => { setRenameOpen(false); setNewName(""); }}
              className="h-9 px-3 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 font-mono uppercase text-[11px] tracking-wider"
            >
              CANCEL
            </button>
          </div>
        ) : (
          <button
            data-testid="collab-rename-open"
            onClick={() => setRenameOpen(true)}
            className="h-9 px-3 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 font-mono uppercase text-[11px] tracking-wider"
          >
            EDIT_MY_NAME
          </button>
        )}
      </div>
    </div>
  );
}

function RosterRow({ label, email, name, badge, tone = "zinc", testid }) {
  const toneMap = {
    amber: "border-amber-500/40 text-amber-300",
    purple: "border-purple-500/40 text-purple-300",
    cyan: "border-cyan-500/40 text-cyan-300",
    zinc: "border-zinc-700 text-zinc-400",
  };
  const cls = toneMap[tone] || toneMap.zinc;
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-2 px-2 py-2 border border-zinc-800 bg-[#0E0E0E]"
    >
      {badge && (
        <span className="inline-flex items-center justify-center h-5 w-5 border border-amber-400 text-amber-300 font-mono text-[10px] font-extrabold">
          {badge}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-mono text-white truncate">
          {name || email || <span className="text-zinc-500">—</span>}
        </div>
        {email && (
          <div className="text-[10px] font-mono text-zinc-500 truncate">
            {email}
          </div>
        )}
      </div>
      <span className={`px-1.5 py-px text-[9px] font-mono uppercase tracking-wider border ${cls}`}>
        {label}
      </span>
    </div>
  );
}
